import { useEffect, useState } from 'react'
import { useTranslation } from '@/contexts/TranslationContext'
import type { WhatsAppMessage } from '@/lib/services/billing'
import { getMessagesForEntry } from '@/lib/services/billing'

interface WhatsAppHistoryBadgeProps {
  entryId: string
}

/**
 * Badge showing WhatsApp message history for a financial entry.
 * Displays counts of charges sent and duedate alerts, with tooltip showing last send time.
 */
export function WhatsAppHistoryBadge({ entryId }: WhatsAppHistoryBadgeProps) {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<WhatsAppMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        console.log('[WhatsAppHistoryBadge] Loading messages for entryId:', entryId)
        const msgs = await getMessagesForEntry(entryId)
        console.log('[WhatsAppHistoryBadge] Messages loaded:', msgs)
        setMessages(msgs)
        setError(null)
      } catch (err) {
        console.error('[WhatsAppHistoryBadge] Failed to load WhatsApp messages:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [entryId])

  if (loading) return null

  const charges = messages.filter(m => m.type === 'charge')
  const alerts = messages.filter(m => m.type === 'duedate_alert')

  if (messages.length === 0) return null

  const lastChargeTime = charges.length > 0 ? new Date(charges[0].sent_at).toLocaleString('pt-BR') : null
  const lastAlertTime = alerts.length > 0 ? new Date(alerts[0].sent_at).toLocaleString('pt-BR') : null

  const te = (key: string, params?: Record<string, string>) =>
    t(`billing.entries.whatsappHistory.${key}`, params)

  return (
    <div className="flex gap-2 flex-wrap items-center">
      {charges.length > 0 && (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
          title={te('chargeTooltip', { time: lastChargeTime || '' })}
        >
          💬 {te('chargeCount', { count: String(charges.length) })}
        </span>
      )}
      {alerts.length > 0 && (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
          title={te('alertTooltip', { time: lastAlertTime || '' })}
        >
          🔔 {te('alertCount', { count: String(alerts.length) })}
        </span>
      )}
      {error && (
        <span className="text-xs text-red-500 dark:text-red-400" title={error}>
          ⚠️ Error loading messages
        </span>
      )}
    </div>
  )
}
